import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { PaymentCategory } from '@/types/payment';
import { useDojoPaymentMethods } from '@/hooks/useDojoPaymentMethods';

interface PaymentMethod {
  id: string;
  name: string;
  label: string;
}

interface PaymentSettings {
  defaultCurrency: string;
  availableCurrencies: string[];
  availablePaymentMethods: PaymentMethod[];
}

interface PaymentCategoryFormProps {
  onAddCategory: (category: Omit<PaymentCategory, 'id'>) => void;
  settings: PaymentSettings;
}

const PaymentCategoryForm: React.FC<PaymentCategoryFormProps> = ({
  onAddCategory,
  settings
}) => {
  useUiLanguage();
  const { t } = useTranslation(['payments', 'common']);
  const { availablePaymentMethods, defaultCurrency, isLoading: paymentMethodsLoading } = useDojoPaymentMethods();

  const [newCategory, setNewCategory] = useState({
    name: '',
    oneYear: 0,
    sixMonths: 0,
    threeMonths: 0,
    monthly: 0,
    currency: 'USD', // Will be updated by effect
    paymentMethods: ['bank_transfer'],
    isActive: true,
    stripeIntegrationEnabled: false,
    stripeLink: ''
  });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const { toast } = useToast();

  // Update currency when defaultCurrency changes
  React.useEffect(() => {
    if (defaultCurrency) {
      setNewCategory(prev => ({ ...prev, currency: defaultCurrency }));
    }
  }, [defaultCurrency]);

  const handleAddCategory = () => {
    if (!newCategory.name.trim()) {
      toast({
        title: t('common:messages.error'),
        description: t('forms:validation.required'),
        variant: "destructive"
      });
      return;
    }

    onAddCategory({
      name: newCategory.name,
      amounts: {
        oneYear: newCategory.oneYear,
        sixMonths: newCategory.sixMonths,
        threeMonths: newCategory.threeMonths,
        monthly: newCategory.monthly
      },
      currency: newCategory.currency,
      paymentMethods: newCategory.paymentMethods,
      isActive: newCategory.isActive,
      stripeIntegrationEnabled: newCategory.stripeIntegrationEnabled,
      stripeLink: newCategory.stripeLink
    });

    setNewCategory({
      name: '',
      oneYear: 0,
      sixMonths: 0,
      threeMonths: 0,
      monthly: 0,
      currency: defaultCurrency || 'USD',
      paymentMethods: ['bank_transfer'],
      isActive: true,
      stripeIntegrationEnabled: false,
      stripeLink: ''
    });
    setShowAddDialog(false);
  };

  return (
    <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-2" />
          {t('common:buttons.add')} {t('payments:categories')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('forms:addNew')} {t('payments:categories')}</DialogTitle>
          <DialogDescription>
            {t('forms:createDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="categoryName">{t('common:labels.name')}</Label>
            <Input
              id="categoryName"
              value={newCategory.name}
              onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
              placeholder={t('forms:placeholders.categoryName')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{uiText("1 Year Subscription")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1000000000"
                value={newCategory.oneYear}
                onChange={(e) => setNewCategory({...newCategory, oneYear: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>{uiText("6 Months Subscription")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1000000000"
                value={newCategory.sixMonths}
                onChange={(e) => setNewCategory({...newCategory, sixMonths: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>{uiText("3 Months Subscription")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1000000000"
                value={newCategory.threeMonths}
                onChange={(e) => setNewCategory({...newCategory, threeMonths: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>{uiText("Monthly Subscription")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1000000000"
                value={newCategory.monthly}
                onChange={(e) => setNewCategory({...newCategory, monthly: parseFloat(e.target.value) || 0})}
              />
            </div>
          </div>

          <div>
            <Label>{uiText("Currency")}</Label>
            <Select
              value={newCategory.currency}
              onValueChange={(value) => setNewCategory({...newCategory, currency: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {settings.availableCurrencies.map(currency => (
                  <SelectItem key={currency} value={currency}>{currency}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{uiText("Payment Methods")}</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {availablePaymentMethods.map(method => (
                <div key={method.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={method.id}
                    checked={newCategory.paymentMethods.includes(method.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setNewCategory({
                          ...newCategory,
                          paymentMethods: [...newCategory.paymentMethods, method.id]
                        });
                      } else {
                        setNewCategory({
                          ...newCategory,
                          paymentMethods: newCategory.paymentMethods.filter(m => m !== method.id)
                        });
                      }
                    }}
                  />
                  <label htmlFor={method.id} className="text-sm">{method.label}</label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>{uiText("Stripe Integration")}</Label>
            <div className="space-y-2 mt-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="stripeIntegration"
                  checked={newCategory.stripeIntegrationEnabled}
                  onCheckedChange={(checked) => setNewCategory({
                    ...newCategory,
                    stripeIntegrationEnabled: !!checked
                  })}
                />
                <label htmlFor="stripeIntegration" className="text-sm">
                  {uiText("Enable Stripe payment processing")}</label>
              </div>
              {newCategory.stripeIntegrationEnabled && (
                <div className="ml-6 space-y-2">
                  <Label htmlFor="stripeLink">{uiText("Stripe Payment Link")}</Label>
                  <Input
                    id="stripeLink"
                    type="url"
                    value={newCategory.stripeLink || ''}
                    onChange={(e) => setNewCategory({
                      ...newCategory,
                      stripeLink: e.target.value
                    })}
                    placeholder="https://buy.stripe.com/..."
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>

          <Button onClick={handleAddCategory} className="w-full">
            {t('common:buttons.add')} {t('payments:categories')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentCategoryForm;
